import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Button, Tabs } from "antd";
import { DndProvider } from "react-dnd";
import { useSearchParams } from "react-router-dom";
import { HTML5Backend } from "react-dnd-html5-backend";
import { PlusOutlined } from "@ant-design/icons";
import "../../assets/css/user-management.css";
import UserListing from "./UserListing";
import RoleBasedPermissions from "./roles-management/RoleBasedPermissions";
import { getAllRoles } from "../../actionCreator/roleManagementActionCreator";
import RoleHierarchy from "./roles-management/RoleHierarchy";
import TeamListing from "./team-management/TeamListing";
import { getUserInfo } from "../../utils/utils";
import useUpgradeAlert from "../../common/hooks/useUpgradeAlert";
import { subscriptionPlans, userRoles } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import { getUserPermissions } from "../../actionCreator/userManagementActionCreator";
import { Users, ShieldCheck, ArrowLeft } from "lucide-react";
import UserLogo from "../../common/UserLogo";

const UserManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState();
  const [isAdd, setAdd] = useState(false);
  const tab = searchParams.get("tab");
  const { role } = getUserInfo();
  const showUpgradeAlert = useUpgradeAlert(() => navigate("/pricing"));
  const { subscription, user } = useContext(AuthContext);

  useEffect(() => {
    if (
      role === userRoles?.admin &&
      [subscriptionPlans?.trail].includes(
        subscription?.subscriptionPackage?.name,
      )
    ) {
      showUpgradeAlert();
    } else {
      dispatch(getAllRoles());
      if (user?.id) {
        dispatch(getUserPermissions(user?.id));
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    } else {
      setActiveTab("1");
    }
  }, [tab]);

  return (
    <section className="p-20 h-100 tw-font-inter">
      <div className="user-management tw-font-inter">
        <div className="tw-mb-6 tw-flex tw-items-center tw-gap-4">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-p-2 tw-bg-slate-100 hover:tw-bg-slate-200/60 tw-text-slate-600 tw-transition-colors"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
          </div>
          <div className="tw-flex tw-flex-col tw-justify-center tw-gap-0.5">
            <h1 className="tw-text-xl tw-font-bold tw-mb-0 tw-text-slate-800 tw-font-inter">
              User Management & Access Control System
            </h1>
            <p className="tw-text-xs tw-mb-0 tw-text-slate-500 tw-font-medium">
              Manage your account users, create teams/departments, configure role hierarchies, and define role-based access permissions.
            </p>
          </div>
        </div>
        <div id="tabscompany">
          <Tabs
            activeKey={activeTab}
            className="tw-font-inter"
            onChange={(val) => {
              const newSearchParams = new URLSearchParams();
              newSearchParams.set("tab", val);

              navigate(`${location.pathname}?${newSearchParams.toString()}`, {
                replace: true,
              });
            }}
            items={[
              {
                key: "1",
                label: (
                  <span className="tw-flex tw-items-center gap-10">
                    <UserLogo />
                    User Management
                  </span>
                ),
                children: (
                  <>
                    {(typeof activeTab === "string"
                      ? parseInt(activeTab)
                      : activeTab) === 1 && (
                      <div className="mt20">
                        <UserListing />
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: "2",
                label: (
                  <span className="tw-flex tw-items-center gap-10">
                    <Users size={18} />
                    Team Management
                  </span>
                ),
                children: (
                  <>
                    {(typeof activeTab === "string"
                      ? parseInt(activeTab)
                      : activeTab) === 2 && (
                      <div className="mt20">
                        <TeamListing />
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: "3",
                label: (
                  <span className="tw-flex tw-items-center gap-10">
                    <ShieldCheck size={18} />
                    Permission Management
                  </span>
                ),
                children: (
                  <>
                    {(typeof activeTab === "string"
                      ? parseInt(activeTab)
                      : activeTab) === 3 && (
                      <Tabs
                        defaultActiveKey="1"
                        className="mt20 fit-w-tab tw-permissions-tab tw-font-inter"
                        tabBarExtraContent={
                          <Button
                            className="tw-ml-10 tw-btn-premium tw-my-2"
                            onClick={() => {
                              setAdd(true);
                            }}
                          >
                            <PlusOutlined />
                            Create New Role
                          </Button>
                        }
                        items={[
                          {
                            key: "1",
                            label: "Permission Matrix",
                            children: (
                              <div className="mt20">
                                <DndProvider backend={HTML5Backend}>
                                  <RoleBasedPermissions
                                    setAdd={setAdd}
                                    isAdd={isAdd}
                                  />
                                </DndProvider>
                              </div>
                            ),
                          },
                          {
                            key: "2",
                            label: "Role Hierarchy",
                            children: (
                              <div className="mt20">
                                <DndProvider backend={HTML5Backend}>
                                  <RoleHierarchy />
                                </DndProvider>
                              </div>
                            ),
                          },
                        ]}
                      />
                    )}
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>
    </section>
  );
};

export default UserManager;
